import { ItemsPerPage, ModalConfirmation, notify, useModal } from '@billy/design-system'
import React, { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useVatDeclaration } from '@views/vatDeclarations/context/vatDeclarationContext'

import { queryClient } from '../../../../../../../config/queryClient'
import { CustomEvent } from '../../../../../../../enums/customEvent'
import { ModalId } from '../../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../../enums/queryKeys'
import { useSegment } from '../../../../../../../hooks'
import { useForm } from '../../../../../../../hooks/useForm'
import { Paging } from '../../../../../../../types/metable'
import { dispatchCustomEvent } from '../../../../../../../utils/customEvent'
import { useApplyVatSuggestions } from '../../../hooks/useApplyVatSuggestions'
import { useFetchSuggestions, VatSuggestion } from '../../../hooks/useFetchSuggestions'
import { useFormValues } from '../hooks/useFormValues'
import { VatSuggestionsForm, VatSuggestionsFormSchema, vatSuggestionsFormSchema } from '../utils/formSchema'

interface VatSuggestionsFormContextState {
  Form: React.ComponentType<any>
  submitForm: () => void
  suggestions: VatSuggestion[] | undefined
  isLoading: boolean
  pageSize: ItemsPerPage
  pagination: Paging | undefined
  setPage: (page: number) => void
  setPageSize: (pageSize: ItemsPerPage) => void
  reviewedRowIds: Set<string>
  handleReviewedChange: (rowId: string, checked: boolean) => void
  isRowReviewed: (rowId: string) => boolean
}

const VatSuggestionsFormContext = createContext<VatSuggestionsFormContextState | undefined>(undefined)

interface VatSuggestionsFormContextProviderProps {
  children: ReactNode
  returnToDeclaration: () => void
}

export const VatSuggestionsFormContextProvider = ({
  children,
  returnToDeclaration,
}: VatSuggestionsFormContextProviderProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { startDate, endDate, id: vatDeclarationId } = useVatDeclaration()
  const { organization } = useUserOrganization()
  const { open: openConfirmationModal, close: closeConfirmationModal } = useModal(
    ModalId.ReadyToApproveSuggestionsModal,
  )

  const entryDatePeriod = startDate && endDate ? `dates:${startDate}...${endDate}` : undefined
  const {
    suggestions,
    isLoading: isSuggestionsLoading,
    pageSize,
    pagination,
    setPage,
    setPageSize,
  } = useFetchSuggestions({
    entryDatePeriod,
  })

  const { Form, handleSubmit, reset, setValue } = useForm<VatSuggestionsFormSchema>({
    validationSchema: vatSuggestionsFormSchema,
    defaultValues: {},
  })

  const handleApplySuccess = useCallback(() => {
    closeConfirmationModal()
    /* Will be removed with ember removal, currently needed to refetch data for the declaration view ember-side */
    if (vatDeclarationId) {
      dispatchCustomEvent(CustomEvent.VatDeclarationInvalidated, { salesTaxReturnId: vatDeclarationId })
    }

    track('VAT Final Suggestion Approved (Client)')

    returnToDeclaration()
    queryClient.invalidateQueries(QueryKeys.VatSuggestions)
    queryClient.invalidateQueries(QueryKeys.VatDeclarations)
    notify({
      id: NotificationKeys.VatSuggestionsApplySuccess,
      message: t('copilot.success.applying_suggestions'),
      variant: 'success',
    })
  }, [closeConfirmationModal, returnToDeclaration, t, track, vatDeclarationId])

  const { applyVatSuggestions, isProcessing } = useApplyVatSuggestions({
    onSuccess: handleApplySuccess,
    onError: () => {
      closeConfirmationModal()
      notify({
        id: NotificationKeys.VatSuggestionsApplyError,
        message: t('copilot.error.applying_suggestions'),
        variant: 'error',
      })
    },
  })

  useFormValues({
    suggestions,
    reset,
    setValue,
  })

  const reviewedRowIdsRef = useRef<Set<string>>(new Set())
  const [reviewedRowIds, setReviewedRowIds] = useState<Set<string>>(new Set())

  const handleReviewedChange = useCallback((rowId: string, checked: boolean) => {
    setReviewedRowIds((prev) => {
      const next = new Set(prev)
      checked ? next.add(rowId) : next.delete(rowId)
      reviewedRowIdsRef.current = next
      return next
    })
  }, [])

  const isRowReviewed = useCallback((rowId: string) => {
    return reviewedRowIdsRef.current.has(rowId)
  }, [])

  const getCollectedData = useCallback((formValues: VatSuggestionsForm) => {
    return Object.entries(formValues)
      .filter(([, data]) => data?.isRejected || data?.isApproved)
      .map(([id, data]) => ({
        id,
        isApproved: data.isApproved,
        isReviewed: true,
        selectedTaxRateId: data.suggestedTaxRateId,
      }))
  }, [])

  const applySuggestions = useCallback(
    (data: VatSuggestionsForm) => {
      if (!organization?.id) {
        return
      }
      const collectedData = getCollectedData(data)
      applyVatSuggestions({
        organizationId: organization?.id,
        payload: {
          vatSuggestions: collectedData,
        },
      })
    },
    [applyVatSuggestions, getCollectedData, organization?.id],
  )

  const submitForm = useCallback(() => {
    handleSubmit(applySuggestions)()
  }, [handleSubmit, applySuggestions])

  const onSubmitClick = useCallback(() => {
    openConfirmationModal()
  }, [openConfirmationModal])

  return (
    <VatSuggestionsFormContext.Provider
      value={{
        Form,
        submitForm: onSubmitClick,
        suggestions,
        isLoading: isSuggestionsLoading || isProcessing,
        pageSize,
        pagination,
        setPage,
        setPageSize,
        reviewedRowIds,
        handleReviewedChange,
        isRowReviewed,
      }}
    >
      {children}
      <ModalConfirmation
        cancelLabel={t('cancel')}
        id={ModalId.ReadyToApproveSuggestionsModal}
        message={t('copilot.ready_to_approve_modal.text')}
        okLabel={t('yes')}
        onOk={submitForm}
        title={t('copilot.ready_to_approve_modal.title')}
      />
    </VatSuggestionsFormContext.Provider>
  )
}

export const useVatSuggestionsForm = () => {
  const context = useContext(VatSuggestionsFormContext)

  if (!context) {
    throw new Error('VatSuggestionsFormContext is missing in the module!')
  }

  return context
}
